import { z } from 'zod';
import { NodeTypeSchema } from './nodes/node-types.schema';

/**
 * Canvas Creation Schema
 * Used for validating new canvas creation data
 */
export const CanvasCreateSchema = z.object({
  title: z
    .string()
    .min(1, { message: "Session title is required" })
    .max(255, { message: "Session title must be less than 255 characters" })
    .trim()
    .optional()
    .default('Untitled'),

  slug: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[a-z0-9-]+$/, { message: "Slug can only contain lowercase letters, numbers, and hyphens" })
    .optional(),

  description: z
    .string()
    .max(500, { message: "Description must be less than 500 characters" })
    .optional(),

  projectId: z
    .string()
    .optional(),

  isPublic: z
    .boolean()
    .default(false),

  thumbnailUrl: z
    .string()
    .url()
    .optional()
    .or(z.literal('')),

  nodes: z.any().optional().default([]),
  edges: z.any().optional().default([]),
  config: z.any().optional().default({}),

  tags: z
    .array(z.string().min(1).max(50))
    .max(10, { message: "Maximum 10 tags allowed" })
    .optional()
    .default([])
});

/**
 * Canvas Update Schema
 * Used for validating canvas updates
 */
export const CanvasUpdateSchema = z.object({
  title: z
    .string()
    .min(1, { message: "Session title is required" })
    .max(255, { message: "Session title must be less than 255 characters" })
    .trim()
    .optional(),

  slug: z
    .string()
    .min(1)
    .max(255)
    .regex(/^[a-z0-9-]+$/, { message: "Slug can only contain lowercase letters, numbers, and hyphens" })
    .optional(),

  description: z
    .string()
    .max(500, { message: "Description must be less than 500 characters" })
    .optional(),

  projectId: z
    .string()
    .nullable()
    .optional(), // Allow null to remove from project

  isPublic: z
    .boolean()
    .optional(),

  thumbnailUrl: z
    .string()
    .url()
    .optional()
    .or(z.literal('')),

  nodes: z.any().optional(),
  edges: z.any().optional(),
  config: z.any().optional(),

  tags: z
    .array(z.string().min(1).max(50))
    .max(10, { message: "Maximum 10 tags allowed" })
    .optional()
});

/**
 * Canvas Search/Filter Schema
 * Used for validating canvas search and filter parameters
 */
export const CanvasSearchSchema = z.object({
  query: z
    .string()
    .max(100, { message: "Search query must be less than 100 characters" })
    .optional(),
  
  tags: z
    .array(z.string())
    .max(5, { message: "Maximum 5 tags for filtering" })
    .optional(),
  
  isPublic: z
    .boolean()
    .optional(),
  
  userId: z
    .string()
    .uuid()
    .optional(),
  
  sortBy: z
    .enum(['created', 'updated', 'name', 'popularity'])
    .default('updated'),
  
  sortOrder: z
    .enum(['asc', 'desc'])
    .default('desc'),
  
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .default(20),
  
  offset: z
    .number()
    .int()
    .min(0)
    .default(0)
});

/**
 * Canvas Node Schema
 * Used for validating individual nodes within a canvas
 */
export const CanvasNodeSchema = z.object({
  id: z.string().uuid(),

  // Use centralized node types from nodes schema
  type: NodeTypeSchema,

  position: z.object({
    x: z.number(),
    y: z.number()
  }),

  data: z.record(z.string(), z.unknown()), // Flexible data structure for different node types

  connections: z.object({
    inputs: z.array(z.object({
      nodeId: z.string().uuid(),
      outputKey: z.string()
    })).default([]),

    outputs: z.array(z.object({
      nodeId: z.string().uuid(),
      inputKey: z.string()
    })).default([])
  }).default({ inputs: [], outputs: [] })
});

/**
 * Canvas Edge Schema
 * Used for validating connections between nodes
 */
export const CanvasEdgeSchema = z.object({
  id: z.string().uuid(),
  sourceNodeId: z.string().uuid(),
  targetNodeId: z.string().uuid(),
  sourceHandle: z.string(),
  targetHandle: z.string(),
  type: z.enum(['default', 'step', 'smoothstep', 'straight']).default('default'),
  animated: z.boolean().default(false),
  style: z.record(z.string(), z.unknown()).default({})
});

/**
 * Canvas Configuration Schema
 * Used for validating canvas-level settings and preferences
 * NOTE: Viewport is now stored at the page level (CanvasPage.config.viewport)
 */
export const CanvasConfigSchema = z.object({
  settings: z.object({
    snapToGrid: z.boolean().default(true),
    gridSize: z.number().int().min(5).max(50).default(20),
    autoSave: z.boolean().default(true),
    theme: z.enum(['light', 'dark']).default('light')
  }).default({
    snapToGrid: true,
    gridSize: 20,
    autoSave: true,
    theme: 'light' as const
  }),

  metadata: z.object({
    version: z.string().default('1.0'),
    lastModified: z.date().default(() => new Date()),
    nodeCount: z.number().int().min(0).default(0),
    edgeCount: z.number().int().min(0).default(0)
  }).default({
    version: '1.0',
    lastModified: new Date(),
    nodeCount: 0,
    edgeCount: 0
  })
});

// Canvas Data Schema - Combined nodes, edges, and config
// NOTE: Viewport moved to page level - Canvas config no longer includes viewport
export const CanvasDataSchema = z.object({
  nodes: z.array(CanvasNodeSchema).default([]),
  edges: z.array(CanvasEdgeSchema).default([]),
  config: CanvasConfigSchema.default({
    settings: {
      snapToGrid: true,
      gridSize: 20,
      autoSave: true,
      theme: 'light'
    },
    metadata: {
      version: '1.0',
      lastModified: new Date(),
      nodeCount: 0,
      edgeCount: 0
    }
  })
});

// TypeScript types inferred from schemas
export type CanvasCreateInput = z.infer<typeof CanvasCreateSchema>;
export type CanvasUpdateInput = z.infer<typeof CanvasUpdateSchema>;
export type CanvasSearchInput = z.infer<typeof CanvasSearchSchema>;
export type CanvasNode = z.infer<typeof CanvasNodeSchema>;
export type CanvasEdge = z.infer<typeof CanvasEdgeSchema>;
export type CanvasConfig = z.infer<typeof CanvasConfigSchema>;
export type CanvasData = z.infer<typeof CanvasDataSchema>;

// Database model type
export type CanvasModel = {
  id: string;
  userId: string;
  categoryId?: string;
  name: string;
  description?: string;
  dimensions: {
    width: number;
    height: number;
  };
  data: CanvasData;
  isPublic: boolean;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
  viewCount: number;
  likeCount: number;
};

// Public canvas type (safe for public consumption)
export type CanvasPublic = Omit<CanvasModel, 'userId'> & {
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
};